import produce from 'immer';
import _ from 'lodash';
import {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useLazyQuery} from 'react-apollo';

import {
  DefaultResourceConfig,
  FetchEntityJobsQuery,
  FetchProjectJobsPartialDocument,
  FetchProjectJobsPartialQuery,
  FetchProjectJobsPartialQueryResult,
  PageInfo,
  useAllQueuesLiteQuery,
  useFetchEntityJobsQuery,
  useFetchProjectLaunchQueuesQuery,
  useFetchRunQueueItemFromProjectQuery,
  useRunJobQuery,
  useViewerDestinationProjectsQuery,
  useViews2MetadataQuery,
  useViews2ViewQuery,
} from '../../generated/graphql';
import {useAccountTeamsList} from '../../pages/HomePage/HomePageSidebar/useAccountTeamsList';
import {useViewer} from '../../state/viewer/hooks';
import {propagateErrorsContext} from '../../util/errors';
import {useRampFlagAccountSelector} from '../../util/rampFeatureFlags';
import {AccountSelectorContext} from '../Search/SearchNav/AccountSelectorContextProvider';
import {DEFAULT_LAUNCH_PROJECT_NAME} from './constants';
import {
  getProjectRunIdMap,
  getRunInformation,
  makeQueueOptionsFromQueueQueries,
  Queue,
  RunInfoMap,
} from './Queues/queueUtil';
import {JobOption} from './SelectJob';
import {EntityOptions} from './SelectProject2';
import {CREATE_QUEUE_PLACEHOLDER, QueueOptions} from './SelectQueue';
import {constructQueueOptions, sortQueues} from './utils';

type AccessorFunc = (a: any) => string;
const sortSpecialFirst = (special: string, accessor: AccessorFunc) => {
  return (a: any, b: any) => {
    const l1 = accessor(a);
    const l2 = accessor(b);
    if (l1 === l2) {
      return 0;
    }
    if (l1 === special) {
      return -1;
    }
    if (l2 === special) {
      return 1;
    }
    return caseInsensitiveCompare(l1, l2);
  };
};

export const caseInsensitiveCompare = (a: string, b: string) => {
  const lowerA = a.toLocaleLowerCase();
  const lowerB = b.toLocaleLowerCase();
  return lowerA.localeCompare(lowerB);
};

// Query for the available launch queues and return a sorted array in the select widget format.
export const useEntityQueueOptions = (
  entityName: string,
  projectName: string,
  defaultResourceConfigs?: Record<string, DefaultResourceConfig>,
  includeCreate: boolean = false
) => {
  const configs = defaultResourceConfigs ?? {};
  const projectRunQueuesQuery = useFetchProjectLaunchQueuesQuery({
    variables: {
      entityName,
      projectName,
    },
  });
  const skipEntityQuery = projectName === DEFAULT_LAUNCH_PROJECT_NAME;
  const entityRunQueuesQuery = useFetchProjectLaunchQueuesQuery({
    variables: {
      entityName,
      projectName: DEFAULT_LAUNCH_PROJECT_NAME,
    },
    skip: skipEntityQuery,
  });
  const projectQueues = constructQueueOptions(projectRunQueuesQuery, configs);
  sortQueues(projectQueues);

  const entityQueues = constructQueueOptions(entityRunQueuesQuery, configs);
  sortQueues(entityQueues);
  let queues: QueueOptions = [];
  if (
    entityQueues.length === 0 ||
    projectName === DEFAULT_LAUNCH_PROJECT_NAME
  ) {
    queues = projectQueues;
    if (queues.length === 0 && includeCreate) {
      queues.push(CREATE_QUEUE_PLACEHOLDER);
    }
  } else if (projectQueues.length === 0) {
    queues = entityQueues;
    if (queues.length === 0 && includeCreate) {
      queues.push(CREATE_QUEUE_PLACEHOLDER);
    }
  } else {
    queues = [
      {
        label: 'Launch',
        options: entityQueues,
      },
      {
        label: projectName,
        options: projectQueues,
      },
    ];
  }

  return {
    queues,
    loading: projectRunQueuesQuery.loading || entityRunQueuesQuery.loading,
    error: projectRunQueuesQuery.error || entityRunQueuesQuery.error,
  };
};

// Alternate "GetQueueOptions" (renamed to EntityQueueOptions) that shows all queues, not just the ones for the project.
export const useViewerQueueOptions = (
  jobIsPrivate: boolean,
  currentEntity: string,
  filterInactive: boolean = true
) => {
  const viewer = useViewer();
  const personalEntity = viewer?.username ?? '';
  const enableAccountSelector = useRampFlagAccountSelector();
  const {selectedAccount} = useContext(AccountSelectorContext);

  const allQueuesQuery = useAllQueuesLiteQuery({pollInterval: 5000});

  if (!allQueuesQuery) {
    return {
      queues: [],
      loading: false,
      error: null,
    };
  }

  if (allQueuesQuery.loading) {
    return {
      queues: [],
      loading: true,
      error: null,
    };
  }

  const queueOptions = makeQueueOptionsFromQueueQueries(
    allQueuesQuery,
    filterInactive,
    jobIsPrivate,
    currentEntity,
    personalEntity,
    enableAccountSelector && selectedAccount ? selectedAccount : undefined
  );

  return {
    queues: queueOptions,
    loading: allQueuesQuery.loading,
    error: allQueuesQuery.error,
  };
};

// Runs have a default color, but user can set manually.
// This hook extracts the project's view spec and run color information.
export const useProjectView = (entityName: string, projectName: string) => {
  const queryViewMetadata = useViews2MetadataQuery({
    variables: {
      entityName,
      name: projectName,
      viewType: 'project-view',
    },
  });
  const viewEdges = queryViewMetadata.data?.project?.allViews?.edges ?? [];
  let viewId = '';
  if (viewEdges.length > 0) {
    viewId = viewEdges[0].node?.id ?? '';
  }
  const queryView = useViews2ViewQuery({
    variables: {
      id: viewId,
    },
    skip: !viewId,
  });

  const specStr = queryView.data?.view?.spec ?? '{}';
  const spec = JSON.parse(specStr);
  const customRunColors = spec?.section?.customRunColors ?? {};

  return {
    viewId,
    spec,
    customRunColors,
    loading: queryViewMetadata.loading || queryView.loading,
    error: queryViewMetadata.error || queryView.error,
  };
};

// We pass the entity/project we are in so we can sort them first.
export const useDestinationProjectOptions = (
  entityName: string,
  projectName: string
) => {
  const query = useViewerDestinationProjectsQuery();
  const enableAccountSelector = useRampFlagAccountSelector();
  const {accountTeams, loading: accountTeamsLoading} = useAccountTeamsList();
  const entityEdges = enableAccountSelector
    ? accountTeams?.edges ?? []
    : query.data?.viewer?.teams?.edges ?? [];
  const entitySort = sortSpecialFirst(entityName, a => a.label);
  const options: EntityOptions[] = entityEdges
    .map(entityEdge => {
      const entityLabel = entityEdge.node?.name ?? '';
      const projectEdges = entityEdge.node?.projects?.edges ?? [];
      const projectSort =
        entityLabel === entityName
          ? sortSpecialFirst(projectName, a => a.projectName)
          : (a: any, b: any) =>
              caseInsensitiveCompare(a.projectName, b.projectName);
      const projectOptions = projectEdges
        .map(projectEdge => {
          const projectId = projectEdge.node?.id ?? '';
          const projectLabel = projectEdge.node?.name ?? '';
          return {
            value: `${entityLabel}/${projectLabel}`,
            entityName: entityLabel,
            projectId,
            projectName: projectLabel,
          };
        })
        .sort(projectSort);
      return {
        label: entityLabel,
        options: projectOptions,
      };
    })
    .sort(entitySort);
  return {
    options,
    loading: query.loading || accountTeamsLoading,
    error: query.error,
  };
};

export const useRunInfoMapFromQueues = (queueOrQueues: Queue | Queue[]) => {
  const [runInfoMap, setRunInfoMap] = useState<RunInfoMap | null>(null);

  const queues = useMemo(
    () => (queueOrQueues instanceof Array ? queueOrQueues : [queueOrQueues]),
    [queueOrQueues]
  );

  // Hack to force polling, since run info may
  // change independently of queues
  const [pollCount, setPollCount] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setPollCount(pollCount + 1);
    }, 5000);
    return () => clearInterval(interval);
  }, [pollCount]);

  useEffect(() => {
    let mounted = true;
    getRunInformation(getProjectRunIdMap(queues))
      .then(map => {
        if (mounted) {
          setRunInfoMap(map);
        }
      })
      .catch(e => {
        if (mounted) {
          console.error(e);
          setRunInfoMap({});
        }
      });

    return () => {
      mounted = false;
    };
  }, [pollCount, queues]); // N.B. pollCount for trigger only.

  return runInfoMap;
};

const getJobOptions = (data: FetchProjectJobsPartialQuery): JobOption[] => {
  const options = [];
  const collectionEdges =
    data?.project?.artifactType?.artifactCollections?.edges ?? [];
  for (const collectionEdge of collectionEdges) {
    // Can only run a Job if it has versions associated with it.
    // It might not if it was created through the Artifacts UI.
    const artifactCollection = collectionEdge.node;
    const versionEdges = artifactCollection?.artifacts?.edges ?? [];
    if (versionEdges.length) {
      options.push({
        value: artifactCollection?.id ?? '',
        label: artifactCollection?.name ?? '',
      });
    }
  }
  return _.sortBy(options, 'label');
};

const getJobOptionsForEntity = (data: FetchEntityJobsQuery) => {
  const options = [];
  const projectEdges = data?.entity?.projects?.edges ?? [];
  for (const projectEdge of projectEdges) {
    const projectName = projectEdge?.node?.name;
    const collectionEdges =
      projectEdge?.node?.artifactType?.artifactCollections?.edges ?? [];
    for (const collectionEdge of collectionEdges) {
      const artifactCollection = collectionEdge.node;
      const versionEdges = artifactCollection?.artifacts?.edges ?? [];
      if (versionEdges.length && projectName != null) {
        options.push({
          value: artifactCollection?.id ?? '',
          label:
            artifactCollection?.name != null
              ? `${projectName}/${artifactCollection.name}`
              : '',
        });
      }
    }
  }
  return options;
};

const INCREMENTAL_BATCH_SIZE = 20;
const INCREMENTAL_MAX_RETRIES = 10;
const INCREMENTAL_MAX_EBO_SCALE = 6;
export interface FetchProjectJobsResult {
  data: FetchProjectJobsPartialQueryResult['data'];
  error: Error | null;
  loading: boolean; // Are we loading initial data?
  loadingMore: boolean; // Are we loading more data?
  refetch: () => void;
}
export const useFetchProjectJobsIncrementally = (
  entityName: string,
  projectName: string
): FetchProjectJobsResult => {
  // Fetched naively, this query can be quite expensive because it can potentially
  // fan out to 500 artifact collections with 500 artifacts each, based on
  // current backend limits.  We fetch incrementally to avoid this, effectively
  // amortizing the cost across multiple requests. Also allows the backend to
  // correctly return more than 500 artifact collections.  Batch size should be
  // tuned such that we process ~1RPS for the sake of app liveness.

  const lastPageInfo = useRef<PageInfo | undefined>(undefined);
  const retries = useRef(0);
  const mounted = useRef(true);

  const [result, setResult] =
    useState<FetchProjectJobsResult['data']>(undefined);
  const [err, setErr] = useState<Error | null>(null);

  const [fetchJobsIncrementally, {loading: loadingMore}] = useLazyQuery(
    FetchProjectJobsPartialDocument,
    {
      onError: e => {
        if (!mounted.current) {
          return;
        }

        if (e.networkError == null) {
          // Treat gql errors as fatal
          setErr(e);
          return;
        }

        // On net error only, retry with exponential backoff, up to 64 seconds/retry
        retries.current++;
        if (retries.current > INCREMENTAL_MAX_RETRIES) {
          setErr(
            new Error(
              `Max retries exceeded (${retries.current}). Last error: ${e.networkError.message}`
            )
          );
          return;
        }

        setTimeout(() => {
          if (!mounted.current) {
            return;
          }

          fetchJobsIncrementally({
            variables: {
              entityName,
              projectName,
              limit: INCREMENTAL_BATCH_SIZE,
              after: lastPageInfo.current?.endCursor ?? '',
            },
          });
        }, 1000 * 2 ** Math.min(retries.current, INCREMENTAL_MAX_EBO_SCALE));
      },
      onCompleted: data => {
        if (!mounted.current) {
          return;
        }

        retries.current = 0;
        const newItems =
          data.project?.artifactType?.artifactCollections?.edges ?? [];
        setResult(old => {
          // First result, return the data as usual
          if (old == null) {
            return data;
          }

          // Subsequent results, merge the new collections into existing data
          return produce(old, draft => {
            draft?.project?.artifactType?.artifactCollections?.edges.push(
              ...newItems
            );
          });
        });
        lastPageInfo.current =
          data.project?.artifactType?.artifactCollections.pageInfo;
        if (lastPageInfo.current?.hasNextPage) {
          fetchJobsIncrementally({
            variables: {
              entityName,
              projectName,
              limit: INCREMENTAL_BATCH_SIZE,
              after: lastPageInfo.current?.endCursor ?? '',
            },
          });
        }
      },
      variables: {
        entityName,
        projectName,
        limit: INCREMENTAL_BATCH_SIZE,
        after: '',
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const fetchFresh = () => {
    setResult(undefined);
    fetchJobsIncrementally();
  };

  // only run on first mount or project changes
  useEffect(() => {
    fetchFresh();
    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityName, projectName]);

  return {
    error: err,
    data: result,
    loading: result == null,
    loadingMore,
    refetch: fetchFresh,
  };
};

export const useAvailableJobOptions = (
  entityName: string,
  projectName: string
): {jobOptions: JobOption[]; loading: boolean} => {
  const isInEntityLaunchProject = projectName === DEFAULT_LAUNCH_PROJECT_NAME;
  const projectJobsQuery = useFetchProjectJobsIncrementally(
    entityName,
    projectName
  );
  const entityJobsQuery = useFetchEntityJobsQuery({
    variables: {
      entityName,
    },
    skip: !isInEntityLaunchProject,
  });

  const noResults =
    projectJobsQuery.loading ||
    projectJobsQuery.error ||
    entityJobsQuery.loading ||
    entityJobsQuery.error;
  const entityData = entityJobsQuery.data;
  const projectData = projectJobsQuery.data;

  return useMemo(() => {
    if (noResults) {
      return {jobOptions: [] as JobOption[], loading: true};
    }
    const jobOptions = isInEntityLaunchProject
      ? getJobOptionsForEntity(entityData!)
      : getJobOptions(projectData!);
    return {
      jobOptions,
      loading: false,
    };
  }, [noResults, isInEntityLaunchProject, entityData, projectData]);
};

export const useFetchRunJob = ({
  entityName,
  projectName,
  runName,
  skip,
}: {
  entityName: string;
  projectName: string;
  runName: string;
  skip: boolean;
}) => {
  const runJobQuery = useRunJobQuery({
    variables: {
      projectName,
      entityName,
      runName,
    },
    skip,
  });

  const job = runJobQuery.data?.project?.run?.job;
  return useMemo(() => {
    return {
      jobInfo:
        job == null
          ? null
          : {
              id: job.artifactSequence.id,
              versionStr: `v:${job.versionIndex}`,
              name: job.artifactSequence.name,
              versionedJobName: `${job.artifactSequence.name} v:${job.versionIndex}`,
            },
      loading: runJobQuery.loading,
      error: runJobQuery.error,
    };
  }, [job, runJobQuery.loading, runJobQuery.error]);
};

export const useRunQueueItem = (
  entityName: string,
  projectName: string,
  runQueueName: string,
  runQueueItemID: string
) => {
  const {data, loading, error} = useFetchRunQueueItemFromProjectQuery({
    variables: {
      entityName,
      projectName,
      runQueueName,
      runQueueItemID,
    },
    skip: !runQueueItemID || !runQueueName,
    context: propagateErrorsContext(),
  });

  return useMemo(() => {
    return {
      runQueueItem:
        loading || error
          ? null
          : {
              ...data?.project?.runQueue?.runQueueItem,
              queueId: data?.project?.runQueue?.id,
            },
      loading,
      error,
    };
  }, [
    loading,
    error,
    data?.project?.runQueue?.runQueueItem,
    data?.project?.runQueue?.id,
  ]);
};
